import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import {
  DEFAULT_ASSET_CLASS_OPTIONS,
  dynamicFormFields,
} from "./edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { useTranslation } from "react-i18next";

const TABLE_NAME = Tables.C5_STATIC_DATA;

const filterC5StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

export type FilterC5StaticDatumsSchema = z.infer<
  typeof filterC5StaticDatumsSchema
>;

const filterC5StaticDatumsFields = filterC5StaticDatumsSchema.keyof().enum;

const assetDepFields: readonly DepField<FilterC5StaticDatumsSchema>[] = [
  {
    name: filterC5StaticDatumsFields.name_contains,
    key: dynamicFormFields.id,
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterC5StaticDatumsFields.isin_contains,
    key: dynamicFormFields.isin,
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5StaticDatumsFields.code_contains,
    key: dynamicFormFields.code,
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC5StaticDatumsFields.ticker_contains,
    key: dynamicFormFields.ticker,
    extract: "text",
    bucket: "contains",
  },
];

export function FilterC5StaticDatums() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: filterC5StaticDatumsFields.name_contains,
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_STATIC_DATA}`,
      column_name: dynamicFormFields.name,
      filters: makeFiltersBuilder<FilterC5StaticDatumsSchema>(
        assetDepFields,
        filterC5StaticDatumsFields.name_contains,
      ),
    },
    {
      name: filterC5StaticDatumsFields.isin_contains,
      label: t(`${TABLE_NAME}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_STATIC_DATA}`,
      column_name: dynamicFormFields.isin,
      filters: makeFiltersBuilder<FilterC5StaticDatumsSchema>(
        assetDepFields,
        filterC5StaticDatumsFields.isin_contains,
      ),
    },
    {
      name: filterC5StaticDatumsFields.code_contains,
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_STATIC_DATA}`,
      column_name: dynamicFormFields.code,
      filters: makeFiltersBuilder<FilterC5StaticDatumsSchema>(
        assetDepFields,
        filterC5StaticDatumsFields.code_contains,
      ),
    },
    {
      name: filterC5StaticDatumsFields.ticker_contains,
      label: t(`${TABLE_NAME}.searchbox.ticker_contains`, { defaultValue: "Ticker" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C5}/${Tables.C5_STATIC_DATA}`,
      column_name: dynamicFormFields.ticker,
      filters: makeFiltersBuilder(
        assetDepFields,
        filterC5StaticDatumsFields.ticker_contains,
      ),
    },
    {
      name: filterC5StaticDatumsFields.currency_contains,
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterC5StaticDatumsFields.asset_class_1_equals,
      assetSubclassFieldName: filterC5StaticDatumsFields.asset_class_2_equals,
      assetClassLabel: t(`${TABLE_NAME}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${TABLE_NAME}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: Clients.C5,
      staticDataTableName: Tables.C5_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassTranslateKey: dynamicFormFields.asset_class_2,
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
  ];

  return (
    <FilterTable
      schema={filterC5StaticDatumsSchema}
      client={Clients.C5}
      tableName={Tables.C5_STATIC_DATA}
      fields={fields}
    />
  );
}
